import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HandHeart, ArrowLeft, Plus, Check, Clock, Gift,
  Building, Calendar, MapPin, Award, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ServiceLedger() {
  const { user } = useAuth();
  
  const [ledger, setLedger] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    organization: '',
    activity_description: '',
    hours_logged: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (user) fetchLedger();
    else setLoading(false);
  }, [user]);

  const fetchLedger = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/service/ledger`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setLedger(await res.json());
      }
    } catch (err) {
      console.error('Error fetching ledger:', err);
    } finally {
      setLoading(false);
    }
  };

  const logHours = async (e) => {
    e.preventDefault();
    if (!formData.organization || !formData.activity_description || !formData.hours_logged) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/foundation/service/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          hours_logged: parseFloat(formData.hours_logged)
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        setFormData({
          organization: '',
          activity_description: '',
          hours_logged: '',
          date: new Date().toISOString().split('T')[0]
        });
        setShowForm(false);
        fetchLedger();
      }
    } catch (err) {
      toast.error('Failed to log hours');
    } finally {
      setSubmitting(false);
    }
  };

  const nextBenefitProgress = ledger?.next_benefit 
    ? (ledger.total_verified_hours / ledger.next_benefit.threshold_hours) * 100 
    : 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30" data-testid="service-ledger">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/foundation" className="inline-flex items-center text-amber-200 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Foundation
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <HandHeart className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Service Ledger</h1>
          </div>
          <p className="text-amber-100">Track your community service, unlock benefits, build your legacy.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {!user ? (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-12 text-center">
              <HandHeart className="h-12 w-12 text-amber-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-stone-800 mb-2">Log In to Track Your Service</h2>
              <p className="text-stone-600 mb-6">Your service hours unlock benefits and earn badges.</p>
              <Link to="/login">
                <Button className="bg-amber-600 hover:bg-amber-700">Log In</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Stats Overview */}
              <div className="grid sm:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                  <CardContent className="py-6 text-center">
                    <div className="text-3xl font-bold">{ledger?.total_verified_hours || 0}</div>
                    <p className="text-amber-100">Verified Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-6 text-center">
                    <div className="text-3xl font-bold text-amber-600">{ledger?.pending_hours || 0}</div>
                    <p className="text-stone-500">Pending Verification</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-6 text-center">
                    <div className="text-3xl font-bold text-emerald-600">{ledger?.unlocked_benefits?.length || 0}</div>
                    <p className="text-stone-500">Benefits Unlocked</p>
                  </CardContent>
                </Card>
              </div>

              {/* Next Benefit Progress */}
              {ledger?.next_benefit && (
                <Card>
                  <CardContent className="py-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-medium text-stone-800">Next Unlock: {ledger.next_benefit.benefit}</h3>
                        <p className="text-sm text-stone-500">{ledger.hours_to_next} more hours needed</p>
                      </div>
                      <Gift className="h-6 w-6 text-amber-500" />
                    </div>
                    <Progress value={nextBenefitProgress} className="h-3" />
                    <p className="text-right text-sm text-stone-500 mt-2">
                      {ledger.total_verified_hours} / {ledger.next_benefit.threshold_hours} hours
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Log Hours Form */}
              {showForm ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Log Service Hours</CardTitle>
                    <CardDescription>Record your volunteer work</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={logHours} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-stone-700">Organization</label>
                        <Input
                          placeholder="e.g., Local Food Bank"
                          value={formData.organization}
                          onChange={(e) => setFormData({...formData, organization: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-stone-700">Activity Description</label>
                        <Textarea
                          placeholder="Describe what you did..."
                          value={formData.activity_description}
                          onChange={(e) => setFormData({...formData, activity_description: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-stone-700">Hours</label>
                          <Input
                            type="number"
                            step="0.5"
                            min="0.5"
                            placeholder="e.g., 2.5"
                            value={formData.hours_logged}
                            onChange={(e) => setFormData({...formData, hours_logged: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-stone-700">Date</label>
                          <Input
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({...formData, date: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
                          {submitting ? "Saving..." : "Log Hours"}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              ) : (
                <Button onClick={() => setShowForm(true)} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="h-4 w-4 mr-2" /> Log Service Hours
                </Button>
              )}

              {/* Service History */}
              <div>
                <h2 className="text-xl font-bold text-stone-800 mb-4">Service History</h2>
                {ledger?.entries?.length > 0 ? (
                  <div className="space-y-4">
                    {ledger.entries.map((entry, idx) => (
                      <motion.div
                        key={entry.entry_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <Card>
                          <CardContent className="py-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Building className="h-4 w-4 text-amber-500" />
                                  <span className="font-medium text-stone-800">{entry.organization}</span>
                                </div>
                                <p className="text-sm text-stone-600 mb-2">{entry.activity_description}</p>
                                <div className="flex items-center gap-4 text-xs text-stone-400">
                                  <span className="flex items-center">
                                    <Calendar className="h-3 w-3 mr-1" /> {entry.date}
                                  </span>
                                  <span className="flex items-center">
                                    <Clock className="h-3 w-3 mr-1" /> {entry.hours_logged} hours
                                  </span>
                                </div>
                              </div>
                              <Badge className={entry.verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                                {entry.verified ? (
                                  <><Check className="h-3 w-3 mr-1" /> Verified</>
                                ) : (
                                  "Pending"
                                )}
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <Card className="bg-stone-50">
                    <CardContent className="py-12 text-center">
                      <HandHeart className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-500">No service hours logged yet. Start serving your community!</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Benefit Thresholds */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-amber-500" />
                    Benefit Thresholds
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {ledger?.all_thresholds?.map((benefit) => {
                    const isUnlocked = ledger.total_verified_hours >= benefit.threshold_hours;
                    
                    return (
                      <div 
                        key={benefit.threshold_hours}
                        className={`p-3 rounded-lg border ${isUnlocked ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-200'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium text-sm ${isUnlocked ? 'text-emerald-700' : 'text-stone-700'}`}>
                            {benefit.threshold_hours} hours
                          </span>
                          {isUnlocked && <Check className="h-4 w-4 text-emerald-500" />}
                        </div>
                        <p className="text-xs text-stone-500">{benefit.benefit}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Community Steward Badge */}
              <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                <CardContent className="py-6 text-center">
                  <Award className="h-12 w-12 mx-auto mb-3" />
                  <h3 className="font-bold text-lg">Community Steward</h3>
                  <p className="text-amber-100 text-sm">
                    Log 100 verified hours to earn this badge and become eligible for the Ambassador program.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
